import { Injectable } from '@angular/core';
import {
  UserMessageEnum,
  UserMessageModel,
  UserProfileMessageData,
} from '../auth/models/user-message-model';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { OrganizationModel } from '../organizations/models/organization.model';
import { HttpClient } from '@angular/common/http';
import { StorageService, SessionStorageEnum } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class UserMessageService {
  constructor(private http: HttpClient, private storageService: StorageService) {}

  getUserMessage(
    type: UserMessageEnum,
    mode: string = '',
    checkCache: boolean = true,
  ): Observable<UserMessageModel> {
    const cachedMessage = this.getCachedMessage(type);
    if (checkCache && cachedMessage) {
      return of(cachedMessage);
    } else {
      return this.http.get(`/ws/user/message?version=2&type=${type}&mode=${mode}`).pipe(
        map((response: any) => {
          const { message_seen_id, show_alert } = response;
          let responseValue: any;
          switch (type) {
            // join organization
            case UserMessageEnum.DomainAlert:
              responseValue = plainToClass(OrganizationModel, response.join_organization as Object);
              break;
            case UserMessageEnum.WelcomeTour:
              responseValue = `${response.tour_version}`;
              break;
            case UserMessageEnum.CompleteProfileAlert:
              const { is_new_user, is_team_user } = response || {};
              responseValue = plainToClass(UserProfileMessageData, { is_new_user, is_team_user });
              break;
          }
          return <UserMessageModel>{
            messageSeenId: message_seen_id,
            messageType: type,
            showUserAlert: show_alert,
            messageData: responseValue,
          };
        }),
        tap((response) => {
          // cache when no user alert needed
          if (!response.showUserAlert) {
            this.addCachedMessage(response);
          }
        }),
      );
    }
  }

  setUserMessageViewed(messageSeenId: string) {
    return this.http.post(`/ws/user/message`, {
      version: 2,
      message_seen_id: messageSeenId,
    });
  }

  sendMessage(message: { type: string; id: string }) {
    return this.http.post(`/ws/user/message`, message);
  }

  private getCachedMessage(type: UserMessageEnum): UserMessageModel {
    const seenMessageCache = this.getCachedMessages();
    return seenMessageCache.find((message) => message.messageType === type);
  }

  private getCachedMessages(): UserMessageModel[] {
    const seenMessages = this.storageService.getSessionItem<UserMessageModel[]>(
      SessionStorageEnum.UserMessagesSeen,
    );
    return seenMessages || [];
  }

  private addCachedMessage(type: UserMessageModel) {
    if (!this.getCachedMessage(type.messageType)) {
      const seenMessages = this.getCachedMessages();
      this.storageService.setSessionItem(SessionStorageEnum.UserMessagesSeen, [
        ...seenMessages,
        type,
      ]);
    }
  }
}
